/**
 * A reader's courtesy after an answer («Amén, gracias», «muy bien, gracias»)
 * is not a question. Sent to the RAG it was searched and answered as a topic
 * (the meaning of «amén»), or refused. With follow-ups on, the chat answers it
 * locally instead: no request, no spend, nothing to refuse.
 *
 * Pure: the rules are smoke-tested in acknowledgement.smoke.ts.
 */
import type { AnswerResponse } from "../api/answer-client";

/** Route type of a local acknowledgement turn (never sent by the RAG). */
export const ACKNOWLEDGE_ROUTE = "acknowledge";
/** Route type of the RAG's request for a whole question (Task 186). */
export const CLARIFY_ROUTE = "clarify";

const COURTESY_WORDS = new Set([
  // Spanish
  "gracias", "muchas", "muchísimas", "muchisimas", "mil", "amén", "amen", "ok", "okay", "vale", "bien", "muy",
  "perfecto", "excelente", "genial", "hermoso", "hermosa", "bendiciones", "bendición", "bendicion", "hermano", "hermana",
  "hermanos", "entendido", "entiendo", "claro", "listo", "bueno", "shalom", "todá", "toda", "que", "te", "les", "lo",
  "bendiga", "bendice", "yahweh", "a", "ti", "usted", "y", "por", "todo", "la", "respuesta", "información", "informacion",
  "aleluya", "halleluyah", "hallelujah", "sí", "si", "de", "nada", "mucho", "maravilloso",
  // English
  "thanks", "thank", "you", "so", "much", "great", "perfect", "got", "it", "blessings", "bless", "amazing", "nice",
  "good", "very", "for", "the", "answer", "understood", "awesome",
]);
// At least one of these must appear: «muy bien» alone is not clearly a thank-you.
const THANKS = /\b(gracias|amén|amen|todá|bendiciones|bendiga|shalom|aleluya|halleluyah|hallelujah|thanks|thank|blessings|bless|perfecto|excelente|genial|entendido|perfect|great|understood)\b/i;

/** True for a short courtesy with no question in it. */
export function isAcknowledgement(text: string): boolean {
  if (/[?¿]/.test(text)) return false;
  const tokens = text.toLowerCase().replace(/[¡!.,;:«»"“”()…🙏❤️]/gu, " ").split(/\s+/).filter(Boolean);
  if (tokens.length === 0 || tokens.length > 8) return false;
  if (!THANKS.test(text)) return false;
  return tokens.every((t) => COURTESY_WORDS.has(t));
}

/** The local reply to a courtesy, shaped like an answer with no sources. */
export function acknowledgementResponse(text: string, reply: (amen: boolean) => string): AnswerResponse {
  return {
    answer: reply(/\bam[eé]n\b/i.test(text)),
    sources: [],
    route: { type: ACKNOWLEDGE_ROUTE, references: [] },
    cache: { hit: false, key_hash: "" },
    model: { name: "none", provider: "none" },
  };
}
